import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';
import {
  colors,
  radius,
  shadow,
  spacing,
  typography,
  transition,
} from 'design-system';

function QuestionNavigator({
  summaryQuestions = [],
  currentPage,
  setCurrentPage,
}) {
  const { answered, total } = useMemo(() => {
    const t = summaryQuestions.length;
    const a = summaryQuestions.filter((q) => q.stateclass !== 'notyetanswered').length;
    return { answered: a, total: t };
  }, [summaryQuestions]);

  const progressPct = total > 0 ? Math.round((answered / total) * 100) : 0;

  return (
    <Wrap>
      <Header>
        <Title>Chuyển đến câu</Title>
        <Counter>
          <CounterValue>{answered}</CounterValue>
          <CounterSep>/</CounterSep>
          <CounterTotal>{total}</CounterTotal>
        </Counter>
      </Header>

      <ProgressBar role='progressbar' aria-valuenow={progressPct} aria-valuemin={0} aria-valuemax={100}>
        <ProgressFill style={{ width: `${progressPct}%` }} />
      </ProgressBar>

      <Grid>
        {summaryQuestions.map((q, index) => {
          const isAnswered = q.stateclass !== 'notyetanswered';
          const isCurrent = index === currentPage;
          return (
            <Bubble
              key={q.slot}
              type='button'
              $current={isCurrent}
              $answered={isAnswered}
              onClick={() => setCurrentPage(index)}
              aria-label={`Câu ${index + 1}${isAnswered ? ' (đã trả lời)' : ''}${isCurrent ? ' (đang xem)' : ''}`}
              aria-current={isCurrent ? 'true' : undefined}
            >
              {index + 1}
            </Bubble>
          );
        })}
      </Grid>

      <Legend>
        <LegendItem>
          <LegendSwatch $variant='current' />
          <span>Đang xem</span>
        </LegendItem>
        <LegendItem>
          <LegendSwatch $variant='answered' />
          <span>Đã trả lời</span>
        </LegendItem>
        <LegendItem>
          <LegendSwatch $variant='unanswered' />
          <span>Chưa trả lời</span>
        </LegendItem>
      </Legend>
    </Wrap>
  );
}

export default QuestionNavigator;

/* ─── styled ─── */

// Sticky positioning được handle ở parent (RightRail trong test detail
// page) — Submit + Navigator stick chung 1 khối. Không nested sticky ở
// đây để tránh xung đột.
const Wrap = styled.div`
  background: ${colors.surface};
  border: 1px solid ${colors.border};
  border-radius: ${radius.lg};
  box-shadow: ${shadow.sm};
  padding: ${spacing[4]};
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: ${spacing[2]};
`;

const Title = styled.h5`
  font-size: ${typography.size.base};
  font-weight: ${typography.weight.semibold};
  color: ${colors.gray900};
  margin: 0;
`;

const Counter = styled.div`
  display: inline-flex;
  align-items: baseline;
  gap: 2px;
  font-size: ${typography.size.sm};
  color: ${colors.gray500};
  font-variant-numeric: tabular-nums;
`;

const CounterValue = styled.span`
  font-size: ${typography.size.base};
  font-weight: ${typography.weight.bold};
  color: ${colors.primary};
`;

const CounterSep = styled.span`
  color: ${colors.gray400};
`;

const CounterTotal = styled.span`
  color: ${colors.gray500};
`;

const ProgressBar = styled.div`
  height: 4px;
  width: 100%;
  background: ${colors.gray100 || colors.surfaceMuted || '#eef0f4'};
  border-radius: ${radius.full || '999px'};
  overflow: hidden;
  margin-bottom: ${spacing[3]};
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, ${colors.success} 0%, ${colors.primary} 100%);
  transition: width 300ms ease;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(36px, 1fr));
  gap: ${spacing[2]};
  margin-bottom: ${spacing[3]};
`;

// 3 trạng thái rạch ròi qua $current / $answered:
//   current  → primary fill + ring đậm, đè lên cả answered
//   answered → success soft + viền success, text success đậm
//   unanswered (default) → viền gray, text gray
const Bubble = styled.button`
  appearance: none;
  border: 1px solid ${colors.border};
  background: ${colors.surface};
  color: ${colors.gray700};
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.semibold};
  font-variant-numeric: tabular-nums;
  width: 100%;
  aspect-ratio: 1 / 1;
  min-height: 36px;
  border-radius: ${radius.md};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: ${transition?.fast || 'all 150ms ease'};

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${shadow.sm};
    border-color: ${colors.primary};
    color: ${colors.primary};
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }

  ${(p) =>
    p.$answered &&
    !p.$current &&
    css`
      background: ${colors.successSoft || 'rgba(34,197,94,0.10)'};
      border-color: ${colors.success};
      color: ${colors.success};

      &:hover {
        border-color: ${colors.success};
        color: ${colors.success};
      }
    `}

  ${(p) =>
    p.$current &&
    css`
      background: ${colors.primary};
      border-color: ${colors.primary};
      color: #fff;
      box-shadow: 0 0 0 3px ${colors.primarySoft || 'rgba(99,102,241,0.25)'};

      &:hover {
        background: ${colors.primary};
        border-color: ${colors.primary};
        color: #fff;
        transform: translateY(-1px);
      }
    `}
`;

const Legend = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  padding-top: ${spacing[3]};
  border-top: 1px solid ${colors.border};
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  font-size: ${typography.size.xs};
  color: ${colors.gray500};
`;

const LegendSwatch = styled.span`
  width: 14px;
  height: 14px;
  border-radius: ${radius.sm || '4px'};
  flex-shrink: 0;

  ${(p) =>
    p.$variant === 'current' &&
    css`
      background: ${colors.primary};
      box-shadow: 0 0 0 2px ${colors.primarySoft || 'rgba(99,102,241,0.25)'};
    `}

  ${(p) =>
    p.$variant === 'answered' &&
    css`
      background: ${colors.successSoft || 'rgba(34,197,94,0.10)'};
      border: 1px solid ${colors.success};
    `}

  ${(p) =>
    p.$variant === 'unanswered' &&
    css`
      background: ${colors.surface};
      border: 1px solid ${colors.border};
    `}
`;
